import { v4 as uuidv4 } from 'uuid';
import { calculateItemTotal, getCurrentUTCDate } from '../../../../../utils';
import { ButtonType } from './invoiceButtonComponents';
import {
  FRAME_HANDLING_ORDER_NEW,
  FRAME_HANDLING_PATIENT_OWN,
  FRAME_HANDLING_STOCK,
} from '../invoice';

export const FRAME_LAB_STATUS_ENCLOSED = 'enclosed';
export const FRAME_LAB_STATUS_TO_COME = 'to come';

const resolveFrameLabStatus = (source = {}) => (
  source.frameLabStatus
  || source?.frameDetails?.frameLabStatus
  || FRAME_LAB_STATUS_ENCLOSED
);

const resolveNumber = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === '') {
      continue;
    }

    const nextValue = Number(value);
    if (!Number.isNaN(nextValue)) {
      return nextValue;
    }
  }

  return 0;
};

const normalizeBrand = (brand) => {
  if (!brand) {
    return null;
  }

  if (typeof brand === 'string') {
    return { name: brand };
  }

  return brand;
};

const normalizeSupplier = (supplier) => {
  if (!supplier) {
    return null;
  }

  if (typeof supplier === 'string') {
    return { supplierName: supplier };
  }

  return supplier;
};

const buildLensDescription = (lensConfig, eye) => {
  const model = lensConfig.model;
  const material = lensConfig.material;
  const index = lensConfig.index;

  const parts = [
    model?.modelName,
    material?.name,
    index?.indexValue,
  ].filter(Boolean);

  return `${parts.join(' - ')} (${eye})`;
};

const buildPatientOwnProperties = (patientOwnFrame = {}) => ({
  eyeSize: patientOwnFrame.eyeSize ?? 0,
  bridgeSize: patientOwnFrame.bridgeSize ?? 0,
  templeLength: patientOwnFrame.templeLength ?? 0,
  aMeasurement: patientOwnFrame.aMeasurement ?? 0,
  bMeasurement: patientOwnFrame.bMeasurement ?? 0,
  dblMeasurement: patientOwnFrame.dblMeasurement ?? 0,
  edMeasurement: patientOwnFrame.edMeasurement ?? 0,
  circumference: patientOwnFrame.circumference ?? 0,
  edAngle: patientOwnFrame.edAngle ?? 0,
  frameColor: patientOwnFrame.frameColor || '',
  frameColorCode: patientOwnFrame.frameColorCode || '',
  frameType: patientOwnFrame.frameType || '',
  sizeCode: patientOwnFrame.sizeCode || '',
  material: patientOwnFrame.material || '',
  shape: patientOwnFrame.shape || '',
  rimType: patientOwnFrame.rimType || '',
});

const inferOrderNewPlaceOrder = (orderNewFrame = {}) => {
  if (typeof orderNewFrame.placeOrder === 'boolean') {
    return orderNewFrame.placeOrder;
  }

  return String(orderNewFrame.orderStatus || orderNewFrame.status || '').toLowerCase() !== 'queued';
};

export const getFrameDetails = (source = {}) => {
  if (!source) {
    return null;
  }

  if (source.frameDetails) {
    return {
      ...source.frameDetails,
      frameLabStatus: resolveFrameLabStatus(source),
    };
  }

  const frameHandling = source.frameHandling || source.mode || FRAME_HANDLING_STOCK;
  const description = source.description || source.name || source.modelNumber || 'Frame';
  const quantity = Math.max(Number(source.quantity || 1) || 1, 1);
  const frameLabStatus = resolveFrameLabStatus(source);

  if (frameHandling === FRAME_HANDLING_PATIENT_OWN) {
    const patientOwnFrame = source.patientOwnFrame || {};

    return {
      mode: FRAME_HANDLING_PATIENT_OWN,
      itemId: null,
      modelNumber: patientOwnFrame.modelNumber || source.modelNumber || null,
      description,
      brand: normalizeBrand(source.brand || patientOwnFrame.brand || null),
      supplier: normalizeSupplier(source.supplier || patientOwnFrame.supplier || null),
      properties: {
        ...(source.properties || {}),
        ...buildPatientOwnProperties(patientOwnFrame),
      },
      listPrice: resolveNumber(source.listPrice, source.price, source.total),
      quantity,
      location: source.location || null,
      isTaxable: typeof source.isTaxable === 'boolean' ? source.isTaxable : true,
      defaultTaxPercentage: resolveNumber(source.defaultTaxPercentage, source.tax),
      frameLabStatus,
      stockFrame: null,
      patientOwnFrame,
      orderNewFrame: null,
    };
  }

  if (frameHandling === FRAME_HANDLING_ORDER_NEW) {
    const orderNewFrame = source.orderNewFrame || {};
    const properties = {
      ...(source.properties || {}),
      ...(orderNewFrame.properties || {}),
      eyeSize: orderNewFrame.eyeSize ?? source.eyeSize ?? orderNewFrame.properties?.eyeSize ?? source.properties?.eyeSize ?? 0,
      bridgeSize: orderNewFrame.bridgeSize ?? source.bridgeSize ?? orderNewFrame.properties?.bridgeSize ?? source.properties?.bridgeSize ?? 0,
      templeLength: orderNewFrame.templeLength ?? source.templeLength ?? orderNewFrame.properties?.templeLength ?? source.properties?.templeLength ?? 0,
      aMeasurement: orderNewFrame.aMeasurement ?? source.aMeasurement ?? orderNewFrame.properties?.aMeasurement ?? source.properties?.aMeasurement ?? 0,
      bMeasurement: orderNewFrame.bMeasurement ?? source.bMeasurement ?? orderNewFrame.properties?.bMeasurement ?? source.properties?.bMeasurement ?? 0,
      dblMeasurement: orderNewFrame.dblMeasurement ?? source.dblMeasurement ?? orderNewFrame.properties?.dblMeasurement ?? source.properties?.dblMeasurement ?? 0,
      edMeasurement: orderNewFrame.edMeasurement ?? source.edMeasurement ?? orderNewFrame.properties?.edMeasurement ?? source.properties?.edMeasurement ?? 0,
      circumference: orderNewFrame.circumference ?? source.circumference ?? orderNewFrame.properties?.circumference ?? source.properties?.circumference ?? 0,
      edAngle: orderNewFrame.edAngle ?? source.edAngle ?? orderNewFrame.properties?.edAngle ?? source.properties?.edAngle ?? 0,
      frameColor: orderNewFrame.frameColor || source.frameColor || orderNewFrame.properties?.frameColor || source.properties?.frameColor || '',
      sizeCode: orderNewFrame.sizeCode || source.sizeCode || orderNewFrame.properties?.sizeCode || source.properties?.sizeCode || '',
      frameType: orderNewFrame.frameType || source.frameType || orderNewFrame.properties?.frameType || source.properties?.frameType || '',
    };

    return {
      mode: FRAME_HANDLING_ORDER_NEW,
      itemId: source.itemId || source.id || null,
      modelNumber: orderNewFrame.modelNumber || source.modelNumber || null,
      description,
      brand: normalizeBrand(orderNewFrame.brand || source.brand || null),
      supplier: normalizeSupplier(orderNewFrame.supplier || source.supplier || null),
      properties,
      listPrice: resolveNumber(orderNewFrame.listPrice, source.listPrice, source.price, source.total),
      quantity: Math.max(Number(orderNewFrame.quantity || source.quantity || 1) || 1, 1),
      location: orderNewFrame.location || source.location || null,
      isTaxable: typeof source.isTaxable === 'boolean' ? source.isTaxable : true,
      defaultTaxPercentage: resolveNumber(source.defaultTaxPercentage, source.tax),
      frameLabStatus,
      stockFrame: null,
      patientOwnFrame: null,
      orderNewFrame: {
        ...orderNewFrame,
        eyeSize: properties.eyeSize,
        bridgeSize: properties.bridgeSize,
        templeLength: properties.templeLength,
        aMeasurement: properties.aMeasurement,
        bMeasurement: properties.bMeasurement,
        dblMeasurement: properties.dblMeasurement,
        edMeasurement: properties.edMeasurement,
        circumference: properties.circumference,
        edAngle: properties.edAngle,
        placeOrder: inferOrderNewPlaceOrder(orderNewFrame),
      },
    };
  }

  const stockFrame = source.stockFrame || source.frame || source.inventoryItem || source;

  return {
    mode: FRAME_HANDLING_STOCK,
    itemId: source.itemId || stockFrame.itemId || stockFrame.id || null,
    modelNumber: source.modelNumber || stockFrame.modelNumber || stockFrame.sku || null,
    description,
    brand: normalizeBrand(source.brand || stockFrame.brand || null),
    supplier: normalizeSupplier(source.supplier || stockFrame.supplier || null),
    properties: {
      ...(stockFrame.properties || source.properties || {}),
    },
    listPrice: resolveNumber(source.listPrice, source.price, stockFrame.listPrice, stockFrame.price, source.total),
    quantity,
    location: source.location || stockFrame.location || null,
    isTaxable: typeof stockFrame.isTaxable === 'boolean'
      ? stockFrame.isTaxable
      : (typeof source.isTaxable === 'boolean' ? source.isTaxable : true),
    defaultTaxPercentage: resolveNumber(stockFrame.defaultTaxPercentage, source.defaultTaxPercentage, source.tax),
    frameLabStatus,
    stockFrame: {
      inventoryItemId: source.itemId || stockFrame.itemId || stockFrame.id || null,
      sku: stockFrame.sku || null,
      status: stockFrame.status || null,
      catalogProductId: stockFrame.catalogProductId || source.catalogProductId || null,
    },
    patientOwnFrame: null,
    orderNewFrame: null,
  };
};

export const mapGroupItemsToGlassesData = (groupItems = [], groupId = null) => {
  const frameItem = groupItems.find((item) => item.type === ButtonType.FRAME || item.type === 'frame');
  const odLens = groupItems.find((item) => item.type === ButtonType.LENS || item.type === 'lens') && groupItems.find((item) => (item.type === ButtonType.LENS || item.type === 'lens') && item.eye === 'OD');
  const osLens = groupItems.find((item) => item.type === ButtonType.LENS || item.type === 'lens') && groupItems.find((item) => (item.type === ButtonType.LENS || item.type === 'lens') && item.eye === 'OS');
  const rx = odLens?.rx || osLens?.rx || null;
  const frameSource = frameItem?.frame || frameItem || null;
  const frameDetails = getFrameDetails(frameItem || frameSource);

  return {
    items: groupItems,
    groupId: groupId || frameItem?.groupId || groupItems[0]?.groupId || null,
    frame: frameItem
      ? {
          ...(frameSource || {}),
          id: frameItem.id || frameSource?.id || null,
          itemId: frameItem.itemId || frameSource?.itemId || null,
          modelNumber: frameItem.modelNumber || frameSource?.modelNumber || frameDetails?.modelNumber || null,
          description: frameItem.description || frameSource?.description || frameDetails?.description || 'Frame',
          brand: frameItem.brand || frameSource?.brand || frameDetails?.brand || null,
          supplier: frameItem.supplier || frameSource?.supplier || frameDetails?.supplier || null,
          properties: frameItem.properties || frameSource?.properties || frameDetails?.properties || {},
          price: frameItem.price ?? frameSource?.price ?? frameDetails?.listPrice ?? 0,
          tax: frameItem.tax ?? frameSource?.tax ?? frameDetails?.defaultTaxPercentage ?? 0,
          defaultTaxPercentage: frameItem.tax ?? frameSource?.defaultTaxPercentage ?? frameDetails?.defaultTaxPercentage ?? 0,
          frameHandling: frameItem.frameHandling || frameSource?.frameHandling || frameDetails?.mode || null,
          frameLabStatus: resolveFrameLabStatus({
            ...frameItem,
            ...frameSource,
            frameDetails,
          }),
          location: frameSource?.location || frameDetails?.location || null,
          frameDetails,
        }
      : null,
    odLens,
    osLens,
    rx
  };
};

export const buildFrameInvoiceItem = (frame, groupId, existingItem = null) => {
  if (!frame) {
    return null;
  }

  const price = resolveNumber(frame.listPrice, frame.price, frame.cost, frame.total, existingItem?.price);
  const tax = resolveNumber(frame.defaultTaxPercentage, existingItem?.tax);
  const discount = resolveNumber(frame.discount, existingItem?.discount);
  const itemId = frame.itemId || frame.id || existingItem?.itemId || null;
  const description = frame.name || frame.modelNumber || frame.description || existingItem?.description || 'Frame';
  const frameDetails = getFrameDetails({
    ...(existingItem || {}),
    ...frame,
    itemId,
    description,
    quantity: frame.quantity || existingItem?.quantity || 1,
    listPrice: price,
    price,
    tax,
    defaultTaxPercentage: tax,
  });
  const frameLabStatus = resolveFrameLabStatus({
    ...frame,
    frameDetails,
  });

  return {
    ...(existingItem || {}),
    id: existingItem?.id || uuidv4(),
    itemId,
    modelNumber: frame.modelNumber || existingItem?.modelNumber || null,
    description,
    brand: frameDetails?.brand || frame.brand || null,
    type: ButtonType.FRAME,
    quantity: frame.quantity || existingItem?.quantity || 1,
    price,
    tax,
    discount,
    total: existingItem
      ? calculateItemTotal({
          ...existingItem,
          quantity: frame.quantity || existingItem.quantity || 1,
          price,
          tax,
          discount
        })
      : 0,
    date: existingItem?.date || getCurrentUTCDate(),
    groupId,
    frameHandling: frame.frameHandling,
    frameLabStatus,
    supplier: frameDetails?.supplier || frame.supplier || null,
    properties: frameDetails?.properties || frame.properties || {},
    itemConfig: frame.frameHandling === FRAME_HANDLING_ORDER_NEW ? null : existingItem?.itemConfig || null,
    frameDetails,
    frame: {
      ...frame,
      frameLabStatus,
      frameDetails,
    }
  };
};

export const buildLensInvoiceItem = (lensConfig, eye, groupId, rx, existingItem = null) => {
  if (!lensConfig) {
    return null;
  }

  const price = resolveNumber(lensConfig.pricing?.totalRetail, existingItem?.price);

  return {
    ...(existingItem || {}),
    id: existingItem?.id || uuidv4(),
    itemId: null,
    description: buildLensDescription(lensConfig, eye),
    type: ButtonType.LENS,
    quantity: 1,
    price,
    tax: 0,
    discount: 0,
    total: existingItem
      ? calculateItemTotal({
          ...existingItem,
          quantity: 1,
          price,
          tax: 0,
          discount: 0
        })
      : 0,
    date: existingItem?.date || getCurrentUTCDate(),
    groupId,
    eye,
    rxId: rx?.id || null,
    rx: rx || existingItem?.rx || null,
    labInstructions: lensConfig.labInstructions || null,
    itemConfig: {
      manufacturerId: lensConfig.manufacturerId,
      lensModelId: lensConfig.lensModelId,
      lensMaterialId: lensConfig.lensMaterialId,
      lensIndexId: lensConfig.lensIndexId,
      treatmentIds: lensConfig.treatmentIds || [],
      manufacturer: lensConfig.manufacturer || null,
      model: lensConfig.model || null,
      material: lensConfig.material || null,
      index: lensConfig.index || null,
      treatments: lensConfig.treatments || []
    }
  };
};

export const buildStandaloneTreatmentItems = (treatments = [], groupId) => (
  (treatments || []).map((treatment) => ({
    id: uuidv4(),
    itemId: treatment.id,
    description: treatment.name || 'Treatment',
    type: ButtonType.TREATMENTS,
    quantity: 1,
    price: resolveNumber(treatment.listPrice),
    tax: resolveNumber(treatment.defaultTaxPercentage),
    discount: 0,
    total: 0,
    date: getCurrentUTCDate(),
    groupId
  }))
);

export const buildGlassesInvoiceItems = ({ frame, lens, treatments, groupId, rx }) => {
  const items = [];

  const frameItem = buildFrameInvoiceItem(frame, groupId);
  if (frameItem) {
    items.push(frameItem);
  }

  const odLensItem = buildLensInvoiceItem(lens?.OD, 'OD', groupId, rx);
  if (odLensItem) {
    items.push(odLensItem);
  }

  const osLensItem = buildLensInvoiceItem(lens?.OS, 'OS', groupId, rx);
  if (osLensItem) {
    items.push(osLensItem);
  }

  return items.concat(buildStandaloneTreatmentItems(treatments, groupId));
};

export const buildUpdatedGlassesInvoiceItems = (existingItems = [], updatedGlasses) => {
  const frameItem = existingItems.find((item) => item.type === ButtonType.FRAME || item.type === 'frame');
  const odLensItem = existingItems.find((item) => (item.type === ButtonType.LENS || item.type === 'lens') && item.eye === 'OD');
  const osLensItem = existingItems.find((item) => (item.type === ButtonType.LENS || item.type === 'lens') && item.eye === 'OS');

  return {
    frameItem: buildFrameInvoiceItem(updatedGlasses.frame, updatedGlasses.groupId, frameItem),
    odLensItem: buildLensInvoiceItem(updatedGlasses.lens?.OD, 'OD', updatedGlasses.groupId, updatedGlasses.rx, odLensItem),
    osLensItem: buildLensInvoiceItem(updatedGlasses.lens?.OS, 'OS', updatedGlasses.groupId, updatedGlasses.rx, osLensItem),
    treatmentItems: buildStandaloneTreatmentItems(updatedGlasses.treatments, updatedGlasses.groupId)
  };
};